import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, postData, updateData } from '../../services/request'

const SUPPORTED_VARIABLES = ['{CandidateName}', '{JobTitle}', '{CompanyName}', '{InterviewLink}', '{ExpiryDate}']

const AdminTemplates = () => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({
    key: '',
    name: '',
    channel: 'EMAIL',
    subject: '',
    body: '',
    isActive: true
  })

  const fetchTemplates = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/templates`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setTemplates(Array.isArray(data) ? data : [])
      } else {
        setTemplates([])
        setError(res.result?.data?.message || res.result?.data?.data || 'Unable to load templates')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  const handleCreate = async () => {
    if (!form.key?.trim() || !form.name?.trim() || !form.body?.trim()) {
      setError('Key, name and body are required')
      return
    }
    const url = `${getAPIBaseURL()}/employme/admin/templates`
    const headers = { 'Content-Type': 'application/json' }
    const res = await postData(url, headers, {
      key: form.key.trim(),
      name: form.name.trim(),
      channel: form.channel,
      subject: form.subject || '',
      body: form.body,
      isActive: form.isActive
    })
    if (res.error === '' && res.result?.status === 201) {
      setShowCreate(false)
      setForm({ key: '', name: '', channel: 'EMAIL', subject: '', body: '', isActive: true })
      fetchTemplates()
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to create template')
    }
  }

  const handleUpdate = async () => {
    if (!editing?.body?.trim()) return
    const url = `${getAPIBaseURL()}/employme/admin/templates/${editing._id}`
    const headers = { 'Content-Type': 'application/json' }
    const res = await updateData(url, headers, {
      name: editing.name,
      channel: editing.channel,
      subject: editing.subject,
      body: editing.body,
      isActive: editing.isActive
    })
    if (res.error === '' && res.result?.status === 200) {
      setEditing(null)
      fetchTemplates()
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update template')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Message Templates</h1>
          <p className="text-sm text-gray-600">
            Manage accept/reject/invite templates and system reminders. Variables: {SUPPORTED_VARIABLES.join(', ')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowCreate(true)
              setError('')
            }}
            className="rounded-lg border border-gray-800 bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
          >
            New template
          </button>
          <Link
            to="/admin/app/employme"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Back to overview
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">Loading...</div>
        ) : templates.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
            No templates yet. Create one to get started.
          </div>
        ) : (
          templates.map((t) => (
            <div
              key={t._id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              {editing?._id === t._id ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <input
                      value={editing.name}
                      onChange={(e) => setEditing((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Name"
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                    <select
                      value={editing.channel}
                      onChange={(e) => setEditing((prev) => ({ ...prev, channel: e.target.value }))}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="EMAIL">EMAIL</option>
                      <option value="SMS">SMS</option>
                      <option value="IN_APP">IN_APP</option>
                    </select>
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={editing.isActive}
                        onChange={(e) => setEditing((prev) => ({ ...prev, isActive: e.target.checked }))}
                      />
                      Active
                    </label>
                  </div>
                  <input
                    value={editing.subject || ''}
                    onChange={(e) => setEditing((prev) => ({ ...prev, subject: e.target.value }))}
                    placeholder="Subject (for email)"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <textarea
                    value={editing.body}
                    onChange={(e) => setEditing((prev) => ({ ...prev, body: e.target.value }))}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdate}
                      className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-gray-900">{t.name}</span>
                      <span className="ml-2 text-xs text-gray-500">({t.key})</span>
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                          t.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {t.channel}
                      </span>
                    </div>
                    <button
                      onClick={() => setEditing({ ...t })}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                  </div>
                  {t.subject && (
                    <div className="mt-2 text-xs text-gray-600">
                      <strong>Subject:</strong> {t.subject}
                    </div>
                  )}
                  <div className="mt-2 whitespace-pre-wrap rounded bg-gray-50 p-2 text-sm text-gray-700">{t.body}</div>
                  <div className="mt-2 text-xs text-gray-500">Updated {formatDate(t.updatedAt)}</div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">New template</h3>
            <div className="space-y-3">
              <input
                value={form.key}
                onChange={(e) => setForm((prev) => ({ ...prev, key: e.target.value }))}
                placeholder="Key (unique, e.g. JOB_ACCEPT)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Name"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <select
                value={form.channel}
                onChange={(e) => setForm((prev) => ({ ...prev, channel: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="EMAIL">EMAIL</option>
                <option value="SMS">SMS</option>
                <option value="IN_APP">IN_APP</option>
              </select>
              <input
                value={form.subject}
                onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder="Subject (for email)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <textarea
                value={form.body}
                onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
                rows={4}
                placeholder="Body. Use variables: {CandidateName}, {JobTitle}, {CompanyName}, {InterviewLink}, {ExpiryDate}"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreate(false)
                  setForm({ key: '', name: '', channel: 'EMAIL', subject: '', body: '', isActive: true })
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default AdminTemplates
